import { apiClient } from '@/utils/api-client'

const API_INTERNAL_BASE = '/projects'

/**
 * Context service for managing contexts and segment associations.
 */
class ContextService {
    /**
     * Get a context by ULID.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} contextUlid - Context ULID
     * @returns {Promise<Object>} Context response
     */
    async get(projectKey, environmentKey, contextUlid) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/contexts/' +
                    contextUlid +
                    '?include=attributes.values,segments'
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Get a context by identifier.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} identifier - Context identifier
     * @returns {Promise<Object>} Context response
     */
    async getByIdentifier(projectKey, environmentKey, identifier) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/contexts/' +
                    identifier +
                    '?ulid=identifier'
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * List contexts for a project environment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @returns {Promise<Object>} Context list response
     */
    async list(projectKey, environmentKey) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/contexts'
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Lookup contexts for search/autocomplete.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @returns {Promise<Object>} Lookup response
     */
    async lookup(projectKey, environmentKey) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/contexts?lookup=true'
            )
            .then((response) => {
                return response.data
            })
    }

    // ********************************************************************************************

    /**
     * Attach a segment to a context.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} contextUlid - Context ULID
     * @param {string} segmentUlid - Segment ULID
     * @returns {Promise<Object>} Attach response
     */
    async attachSegment(projectKey, environmentKey, contextUlid, segmentUlid) {
        return await apiClient
            .post(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/contexts/' +
                    contextUlid +
                    '/segments/' +
                    segmentUlid,
                []
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Detach a segment from a context.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} contextUlid - Context ULID
     * @param {string} segmentUlid - Segment ULID
     * @returns {Promise<Object>} Detach response
     */
    async detachSegment(projectKey, environmentKey, contextUlid, segmentUlid) {
        return await apiClient
            .delete(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/contexts/' +
                    contextUlid +
                    '/segments/' +
                    segmentUlid
            )
            .then((response) => {
                return response.data
            })
    }
}

export default new ContextService()
